/** Shared by the System status sections: what /system/status returns, and the small pieces that show it. */
import { StatusDot } from '../../../components/Status';
import { bytes } from '../../../lib/format';

export interface StatusDTO {
  checkedAt: string;
  /**
   * `action` (1.0.0-rc.2): a button the alert offers, posted to /api/v1/system/alerts/<id>. `key` (1.2.2): what the
   * alert is about, stable while it lasts; Suppress… hides it by this key. Suppressed alerts are not in this list.
   */
  alerts: StatusAlertDTO[];
  /** 1.2.2: every suppressed warning, and whether it is still occurring (absent from servers before 1.2.2). */
  suppressed?: AlertSuppressionDTO[];
  figures: { assets: number; inRecycleBin: number; hoursOfMedia: number; sequences: number; members: number; signedIn: number; workflows: number; watchFolders: number };
  database: { ok: boolean; latencyMs: number; sizeBytes: number; version: string; connections: number };
  outbox: { pending: number; oldest: number };
  services: Array<{ service: string; instance: string; host: string; pid: number | null; version: string | null; startedAt: string; lastSeenAt: string; healthy: boolean; detail: Record<string, unknown> }>;
  replacedInstances: number;
  queues: Array<{ taskQueue: string; kind: string; pollers: number; identities: string[]; lastPollAt: string | null; healthy: boolean }>;
  workers: WorkerPool[];
  transcoder: { configured: boolean; reachable?: boolean; engine?: string; error?: string; info?: { engine: string; simulated?: boolean; version?: string } };
  speech: { configured: boolean; reachable?: boolean; url?: string; error?: string };
  /** 1.3.1: ClamAV (the media portal's virus scanning) when PRISM_PORTAL_CLAMAV_URL is set; null or absent otherwise. */
  clamav?: { configured: boolean; reachable: boolean | null; version: string | null; checkedAt: string | null; latencyMs?: number | null; engine?: string | null; signatures?: string | null; signaturesAt?: string | null; error?: string | null } | null;
  agents: Array<{ name: string; site: string; status: string; connected: boolean; version: string | null; hostname: string | null; lastSeenAt: string | null; queuedJobs: number }>;
  storage: Array<{ name: string; kind: string; availability: string; lastError: string | null; capacity: { freeBytes: number; totalBytes: number } | null; enabled: boolean }>;
  jobs: Record<string, Record<string, number>>;
  errors24h: Record<string, number>;
  /** 1.0.0-rc.13: the workflow service's cache, its admission of ingests and transfers, and its task backlog. */
  workflowService?: {
    cache: { max: number; cached: number; taskConcurrency: number } | null;
    admission: { ingest: { max: number; running: number; deferred: number }; transfer: { max: number; running: number; deferred: number }; perSecond: number } | null;
    backlog: number | null; backlogAgeSeconds: number | null;
  } | null;
}
export interface StatusAlertDTO { level: 'error' | 'warning'; message: string; area: string; key?: string; action?: { id: string; label: string } }
export interface AlertSuppressionDTO {
  key: string; level: 'error' | 'warning'; area: string | null; message: string; active: boolean; note: string | null;
  suppressedBy: { id: string; displayName: string } | null; suppressedAt: string;
}
interface WorkerPool {
  role: 'process' | 'transcode' | 'transfer' | 'live' | 'graphics'; site: string; pool: string; taskQueue: string; label: string; help: string;
  pollers: number; backlog: number | null; backlogAgeSeconds: number | null; healthy: boolean; lastPollAt: string | null; capacity: number; busy: number;
  replicas: Array<{
    instance: string; host: string; healthy: boolean; lastSeenAt: string; startedAt: string; version: string | null; concurrency: number | null; roles: string[];
    completed: number; failed: number;
    running: Array<{ activity: string; label: string; step: string | null; percent: number | null; startedAt: string; attempt: number; subject: { type: string; id: string; name: string; link: string } | null }>;
  }>;
}

export type Section = 'overview' | 'services' | 'workers' | 'logs' | 'jobs' | 'storage' | 'housekeeping' | 'email' | 'graphics';
export interface ResourceUse {
  cpuPercent: number | null; memoryBytes: number; memoryLimitBytes: number | null; scope: 'container' | 'process'; cores: number;
  /** 0.99.1: the container's CPU limit in cores (cgroup cpu.max), and its reclaimable file cache. Older services leave them out. */
  cpuLimitCores?: number; cacheBytes?: number | null;
}

const coresLabel = (n: number) => `${Number.isInteger(n) ? n : n.toFixed(1)} ${n === 1 ? 'core' : 'cores'}`;

export const Dot = ({ ok, warn = false, label }: { ok: boolean; warn?: boolean; label: string }) => (
  <StatusDot tone={ok ? 'ready' : warn ? 'partial' : 'failed'}>{label}</StatusDot>
);

export function Usage({ r }: { r: ResourceUse | undefined }) {
  if (!r) return <span className="faint">—</span>;
  const memShare = r.memoryLimitBytes ? r.memoryBytes / r.memoryLimitBytes : null;
  // The bar is full at the container's own CPU limit (2 CPUs busy of a 2-CPU limit), not at the host's cores.
  const limit = r.cpuLimitCores && r.cpuLimitCores > 0 ? r.cpuLimitCores : r.cores;
  const cpuShare = r.cpuPercent === null ? null : r.cpuPercent / Math.max(1, limit * 100);
  const memTitle = r.cacheBytes
    ? `Used ${bytes(r.memoryBytes)} (+ ${bytes(r.cacheBytes)} file cache, freed when needed)`
    : `Used ${bytes(r.memoryBytes)}`;
  return (
    <div className="usage" title={r.scope === 'container' ? 'The whole container, including FFmpeg' : 'This process'}>
      <span className="usage-row" title={`${r.cpuPercent === null ? 'CPU' : `CPU ${Math.round(r.cpuPercent)} %`} of ${coresLabel(limit)}`}>
        <span className="faint">CPU</span> {r.cpuPercent === null ? '…' : `${Math.round(r.cpuPercent)}%`}{' '}
        <span className="faint">of {coresLabel(limit)}</span>
        {cpuShare !== null && <span className="usage-bar"><span style={{ width: `${Math.min(100, cpuShare * 100)}%` }} /></span>}</span>
      <span className="usage-row" title={memTitle} data-testid="usage-mem"><span className="faint">Mem</span> {bytes(r.memoryBytes)}{r.memoryLimitBytes ? ` / ${bytes(r.memoryLimitBytes)}` : ''}
        {memShare !== null && <span className={`usage-bar ${memShare > 0.9 ? 'hot' : ''}`}><span style={{ width: `${Math.min(100, memShare * 100)}%` }} /></span>}</span>
    </div>
  );
}
